class EnvironmentService {
  private static instance: EnvironmentService

  private static readonly PRODUCTION_HOSTNAME = 'app.attorneyshare.com'

  private static readonly STAGING_HOSTNAME = 'app.attorneyshare.info'

  public static getInstance(): EnvironmentService {
    if (!EnvironmentService.instance) {
      EnvironmentService.instance = new EnvironmentService()
    }
    return EnvironmentService.instance
  }

  /**
   * Check if a query parameter is present and truthy
   * @param paramName - The query parameter name to check
   * @returns true if param exists and is not 'false' or '0'
   */
  private getQueryParam(paramName: string): boolean {
    if (typeof window === 'undefined') {
      return false
    }

    const params = new URLSearchParams(window.location.search)
    const value = params.get(paramName)

    // Return true if param exists and is not explicitly false
    return value !== null && value !== 'false' && value !== '0'
  }

  /**
   * Whether the page is served from the production host, from the hostname
   * alone. Unlike isProduction(), the forceStaging / forceProduction query
   * params don't change the answer, so it is safe for security decisions a
   * visitor must not be able to switch off from the URL.
   */
  public isProductionHost(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.location.hostname === EnvironmentService.PRODUCTION_HOSTNAME
    )
  }

  /**
   * Check if running in production environment
   * Production is determined by:
   * 1. forceProduction query param (overrides hostname)
   * 2. hostname being app.attorneyshare.com
   * Note: forceStaging will make this return false (mutually exclusive)
   */
  public isProduction(): boolean {
    // Check for query param overrides (mutually exclusive)
    if (this.getQueryParam('forceStaging')) {
      return false
    }
    if (this.getQueryParam('forceProduction')) {
      return true
    }

    // Fall back to hostname check
    return (
      typeof window !== 'undefined' &&
      window.location.hostname === EnvironmentService.PRODUCTION_HOSTNAME
    )
  }

  /**
   * Check if running in staging environment
   * Staging is determined by:
   * 1. forceStaging query param (overrides hostname)
   * 2. hostname being app.attorneyshare.info
   * Note: forceProduction will make this return false (mutually exclusive)
   */
  public isStaging(): boolean {
    // Check for query param overrides (mutually exclusive)
    if (this.getQueryParam('forceProduction')) {
      return false
    }
    if (this.getQueryParam('forceStaging')) {
      return true
    }

    // Fall back to hostname check
    return (
      typeof window !== 'undefined' &&
      window.location.hostname === EnvironmentService.STAGING_HOSTNAME
    )
  }
}

export const environmentService = EnvironmentService.getInstance()
